import ProviderAPIClient, { type TrfPdf } from '@/features/auth/ProviderAPIClient'
import type { CanonicalOrderPayload } from '@/ordering/canonicalOrder'

/**
 * Fetch a freshly re-rendered TRF PDF for an already-placed (in-progress) order.
 *
 * The stored submit-time artifact (`trf_pdf_url` on order-details) reflects the order exactly as
 * it was placed. When a provider edits an in-progress order, those edits live in the SPA's
 * canonical (Redux) until they are sent, so the stored artifact is stale. This helper asks
 * provider-api to re-render the TRF from the supplied (current) canonical: the backend renders
 * server-side, writes the transient PDF to S3, and streams the bytes back same-origin
 * (JWT-authenticated) — never a pre-signed S3 URL the browser must fetch cross-origin. Fetching
 * S3 directly is blocked inside the Epic SMART embedded iframe, so streaming through the API is
 * what makes the preview render there. The bytes are handed to the existing `PDFViewer`.
 *
 * The re-render is transient and does not overwrite the order's persisted artifact.
 *
 * @param orderId    Placed order id.
 * @param canonical  Current canonical order to render from (may include unsent edits).
 * @param options    `isEhrOrder` explicit Epic-SMART launch flag forwarded to the endpoint.
 * @param caller     Audit origin for the regenerate request.
 * @param signal     Abort signal so an in-flight fetch is cancelled on unmount / re-fetch.
 */
export async function fetchRegeneratedOrderTrfPdfBytes(
  orderId: string,
  canonical: CanonicalOrderPayload,
  options: { isEhrOrder?: boolean } = {},
  caller: string,
  signal?: AbortSignal
): Promise<TrfPdf> {
  const client = ProviderAPIClient.getInstance()
  return client.regenerateOrderPdfContent(
    orderId,
    {
      canonicalOrder: canonical,
      ...(options.isEhrOrder != null ? { isEhrOrder: options.isEhrOrder } : {}),
    },
    caller,
    signal
  )
}
